import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { BaseComponent } from '../base-component';
import { EventService } from '../service/event.service';
import { Router } from '@angular/router';
import { ChangeNavigationEvent, ValidateAndNavigateEvent, ProgressBarEvent, SkipToMainContentEvent } from '../service/events';
import { AFOUSFinraConsent } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { ConsentService } from '../service/consent.service';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-ous-finra-consent',
  templateUrl: './ous-finra-consent.component.html',
  styleUrls: ['./ous-finra-consent.component.scss'],
  standalone: false
})

export class OusFinraConsentComponent  extends BaseComponent implements OnInit, AfterViewInit {

  signatureImg: string; // @Input wet-signature
  ackData: UntypedFormGroup;
  afDisclosureInfo: AFOUSFinraConsent;
  authLanguageLookup: string;
  ackTerms: string;
  date: any;
  termDate: any;
  sigCheckValue: boolean = false;
  jobCountry: string = "";
  termsCheck: boolean = false;
  hasBeenSubmitted: boolean = false;

  @ViewChild("pdfButton") pdfButton: ElementRef;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;

  constructor(private readonly eventBus: EventService, private readonly _router: Router, private readonly userService: AuthService,
    private readonly consentService: ConsentService, private readonly fb: UntypedFormBuilder, private readonly customValidator: CustomValidatorsService,
    private readonly datepipe: DatePipe, private readonly _snackBar: MatSnackBar) {
    super(eventBus, _router);
  }

  ngOnInit(): void {
    this.jobCountry = this.userService.getJobCountry();
    this.createGroup();
    this.subscribeEvents();
  }

  get output() { return this.ackData.get('output') }
  get isFinraLegalRequirementsMet() { return this.ackData.get('isFinraLegalRequirementsMet') }
  get finraApplicantSignedName() { return this.ackData.get('finraApplicantSignedName') }
  get isFinraTermsConditionsAccepted() { return this.ackData.get('isFinraTermsConditionsAccepted') }
  get bookmarkUrl() { return this.ackData.get('bookmarkUrl') }

  createGroup() {
    this.ackData = this.fb.group({
      output: ['', Validators.required],
      finraApplicantSignedName: ['', [Validators.required, Validators.maxLength(250), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      isFinraLegalRequirementsMet: [false, Validators.requiredTrue],
      isFinraTermsConditionsAccepted: [false, Validators.requiredTrue],
      bookmarkUrl: ['']
    });
  }

  subscribeEvents() {
    this.consentService.getAFOUSFinraConsent().subscribe((data: AFOUSFinraConsent) => {
      this.afDisclosureInfo = data;
      this.ackData.patchValue(data);
      this.signatureImg = data.output;
      this.date = data.finraLegalRequirementsCheckDate;
      this.termDate = data.finraDiscTermsCheckDate;
    });

    this.isFinraLegalRequirementsMet.valueChanges.subscribe(() => {
      this.setDate();
    });

    this.isFinraTermsConditionsAccepted.valueChanges.subscribe(() => {
      this.setTermDate();
    });


    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event: { payload: any }): void => {
        this.bookmarkUrl.setValue("");
        if (event.payload.currentLink === '/ousFinraConsent') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitafDisclosureInfo((this.ackData.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitafDisclosureInfo((this.ackData.value), event);
          } else if (this.validate()) {
            this.submitafDisclosureInfo((this.ackData.value), event);
          }
        }
      }
    );
  }

  ngAfterViewInit() {
    this.ackData.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.ackData);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/ousFinraConsent");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

  }

  downloadAcknowledgmentPDF(): void {
    let fileName = "OUS Finra Aknowledgment";
    this.userService.downloadOusFinraConsentPDF(fileName, this.userService.getJobCountry(), this.userService.getJobSubCountry(), this.userService.getAfId()).subscribe({
      next: () => {
        this.termsCheck = true;
      },
      error: (error: Error) => {
        this.termsCheck = false;
        console.log("Error with downloading PDF: ", error);
      }
    });
  }

  submitafDisclosureInfo(afOUSFinraConsent: AFOUSFinraConsent, event) {
    afOUSFinraConsent.output = this.signatureImg;
    afOUSFinraConsent.afId = this.userService.getAfId();
    afOUSFinraConsent.finraLegalRequirementsCheckDate = this.date;
    afOUSFinraConsent.finraDiscTermsCheckDate = this.termDate;
    afOUSFinraConsent.verifyId = this.userService.getVerifyId();

    this.consentService.saveAfOUSFinraConsent(afOUSFinraConsent).subscribe({
      next: (resp:string) => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else if(resp === "SUCCESS") {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        } else {
          this._snackBar.open("Error Occured during Save & Continue", "Close", {
            duration: 4000,
          });
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  getPercentage(container: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;

    for (let controlKey in container.controls) {
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    return ((totalCount - errorCount) / totalCount) * 100;
  }

  onSubmit(event: any) {
    if(this.validate()) {
      this.submitafDisclosureInfo((this.ackData.value), event);
    }
  }

  validate() {
    this.ackData.markAllAsTouched();
    this.hasBeenSubmitted = true;

    if (!this.output.value) {
      this.output.setErrors({ invalid: true });
    }

    if (!this.termsCheck) {
      this.pdfButtonFocus();

      return false;
    }

    if (this.ackData.invalid) {
      return false;
    }

    return true;
  }

  pdfButtonFocus(): void {
    setTimeout(() => {
      this.pdfButton.nativeElement.focus();
    });
  }

  onFocusOutMainContent() {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.ackData.get('output').setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  setDate() {
    if (this.isFinraLegalRequirementsMet.value) {
      if (!this.date && !this.afDisclosureInfo.finraLegalRequirementsCheckDate) {
        this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
      }
    } else {
      this.date = null;
    }
  }

  setTermDate() {
    if (this.isFinraTermsConditionsAccepted.value) {
      if (!this.termDate && !this.afDisclosureInfo.finraDiscTermsCheckDate) {
          this.termDate = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
      }
    } else {
      this.termDate = null;
    }
  }
}

